import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import moment from 'moment';
import { environment } from '../../../../environments/environment';
import { ButtonComponent } from '../../shared/components/redesign/botao/botao.component';
import { CampoTextoComponent } from '../../shared/components/redesign/campo-texto/campo-texto.component';
import { CampoTextareaComponent } from '../../shared/components/redesign/campo-textarea/campo-textarea.component';
import { CampoDataComponent } from '../../shared/components/redesign/campo-data/campo-data.component';
import { CampoHoraComponent } from '../../shared/components/redesign/campo-hora/campo-hora.component';
import { CampoSelectComponent } from '../../shared/components/redesign/campo-select/campo-select.component';
import { CampoBuscaContatoComponent } from '../../shared/components/redesign/campo-busca-contato/campo-busca-contato.component';
import { CampoSegmentadoComponent, OpcaoSegmentada } from '../../shared/components/redesign/campo-segmentado/campo-segmentado.component';
import { TemplateMensagemService } from '../../shared/service/template-mensagem.service';
import { LembreteService } from '../../shared/service/lembrete.service';
import { MsgService } from '../../shared/service/msg.service';
import { AuthService } from '../../shared/service/auth.service';
import { ValidacaoAlteracaoCampoService } from '../../shared/service/utilitarios/validacao-alteracao-campo.service';
import { ModalConfirmacaoComponent } from '../../shared/components/redesign/modal-confirmacao/modal-confirmacao.component';
import { ModalVizualizadorComponent } from '../../shared/modais/modal-vizualizador/modal-vizualizador.component';
import { Contato } from '../../shared/model/contato/contato.model';
import { LembreteCriacao } from '../../shared/model/lembrete/lembrete-criacao.model';
import { TemplateSalvamentoModel } from '../../shared/model/template/template-salvamento-model';
import { IdNome } from '../../shared/model/id-nome.model';
import { TemplateComponenteExemploModel } from '../../shared/model/template/template-componente-exemplo-model';
import { TipoLembreteEnum, TIPO_LEMBRETE_LABEL } from '../../shared/enum/tipo-lembrete.enum';
import { extrairMensagemErro } from '../../shared/util/http-error.util';
import { ArrastarFecharSheetDirective } from '../../chat/utils/arrastar-fechar-sheet.directive';

@Component({
  selector: 'app-modal-cadastro-lembrete',
  standalone: true,
  imports: [
    ButtonComponent,
    CampoTextoComponent,
    CampoTextareaComponent,
    CampoDataComponent,
    CampoHoraComponent,
    CampoSelectComponent,
    CampoBuscaContatoComponent,
    CampoSegmentadoComponent,
    ReactiveFormsModule,
    MatIconModule,
    ArrastarFecharSheetDirective,
  ],
  templateUrl: './modal-cadastro-lembrete.component.html',
  styleUrl: './modal-cadastro-lembrete.component.css'
})
export class ModalCadastroLembreteComponent implements OnInit {
  readonly TipoLembreteEnum = TipoLembreteEnum;
  readonly dataMinima = moment().startOf('day');
  readonly opcoesTipo: OpcaoSegmentada[] = [
    { valor: TipoLembreteEnum.PARA_MIM, label: TIPO_LEMBRETE_LABEL[TipoLembreteEnum.PARA_MIM] },
    { valor: TipoLembreteEnum.PARA_CLIENTE, label: TIPO_LEMBRETE_LABEL[TipoLembreteEnum.PARA_CLIENTE] },
  ];

  isEdicao = false;
  isVisualizacao = false;
  salvando = false;
  processando = false;
  excluindo = false;
  carregando = false;
  lembretePodeProcessar = false;
  lembretePodeExcluir = false;

  form!: FormGroup;

  contatoSelecionado: Contato | null = null;

  templates: IdNome[] = [];
  carregandoTemplates = false;
  private templatesCarregados = false;
  templateDetalhe: TemplateSalvamentoModel | null = null;
  carregandoTemplateDetalhe = false;
  private versaoTemplateDetalhe = 0;

  etapasOpcoes: any[] = [];
  resolvendoRedirecionamento = false;
  bloqueadoSemEtapa = false;
  mensagemBloqueio = '';

  private readonly dialogRef = inject<MatDialogRef<ModalCadastroLembreteComponent>>(MatDialogRef);
  readonly arrastarParaFechar = this.dialogRef instanceof MatDialogRef;
  public data = inject(MAT_DIALOG_DATA);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(FormBuilder);
  private readonly templateMensagemService = inject(TemplateMensagemService);
  private readonly lembreteService = inject(LembreteService);
  private readonly msgService = inject(MsgService);
  private readonly usuario = inject(AuthService).getUsuarioLogadoClariBot();
  private readonly alteracaoService = inject(ValidacaoAlteracaoCampoService);

  ngOnInit() {
    this.isVisualizacao = !!this.data?.['visualizar'];

    this.form = this.fb.group({
      tipo: [TipoLembreteEnum.PARA_CLIENTE, [Validators.required]],
      titulo: ['', [Validators.required, Validators.minLength(3)]],
      data: [null, [Validators.required]],
      hora: ['', [Validators.required]],
      templateId: [null, [Validators.required]],
      etapaId: [{ value: null, disabled: true }],
      anotacao: ['', [Validators.maxLength(250)]],
    });

    this.alteracaoService.camposNaoAlterados();
    this.alteracaoService.monitorarAlteracoes(this.form);

    this.form.get('tipo')!.valueChanges.subscribe(tipo => this.ajustarValidacaoTemplate(tipo));

    const lembreteId = this.data?.['lembreteId'];
    if (lembreteId) {
      this.isEdicao = true;
      this.carregarLembrete(lembreteId);
    } else {
      if (this.data?.['contato']) this.contatoSelecionado = this.data['contato'];
      this.buscarTemplatesSeNecessario();
    }

    this.alteracaoService.registrarFechamentoComConfirmacao(this.dialogRef, 'Deseja realmente cancelar?');
  }

  private ajustarValidacaoTemplate(tipo: TipoLembreteEnum) {
    const templateControl = this.form.get('templateId')!;
    if (tipo === TipoLembreteEnum.PARA_CLIENTE) {
      templateControl.setValidators([Validators.required]);
      this.buscarTemplatesSeNecessario();
    } else {
      templateControl.clearValidators();
      templateControl.setValue(null);
      this.resetEtapaState();
    }
    templateControl.updateValueAndValidity();
  }

  private resetEtapaState() {
    this.etapasOpcoes = [];
    this.bloqueadoSemEtapa = false;
    this.mensagemBloqueio = '';
    this.resolvendoRedirecionamento = false;
    const etapaControl = this.form.get('etapaId')!;
    etapaControl.setValue(null);
    etapaControl.disable();
  }

  onTemplateSelecionado(template: IdNome | null, cargaInicial = false) {
    this.resetEtapaState();
    this.buscarDetalheTemplate(template?.id);
    if (!template?.id) {
      return;
    }

    this.resolvendoRedirecionamento = true;
    this.lembreteService.resolverRedirecionamentoTemplate(template.id).subscribe({
      next: res => {
        this.resolvendoRedirecionamento = false;
        const etapas = res?.etapas ?? [];

        if (etapas.length === 0) {
          this.bloqueadoSemEtapa = true;
          this.mensagemBloqueio = 'Não é possível enviar este template: nenhuma etapa do fluxo está vinculada a um canal seu. Fale com seu coordenador.';
          if (cargaInicial) this.alteracaoService.camposNaoAlterados();
          return;
        }

        this.etapasOpcoes = etapas.map((e: any) => ({
          ...e,
          descricaoEtapaCanal: `${e.id} - ${e.titulo} | ${e.canal?.nome ?? 'sem canal'}`
        }));

        const etapaControl = this.form.get('etapaId')!;
        if (this.etapasOpcoes.length === 1) {
          etapaControl.setValue(this.etapasOpcoes[0]);
          etapaControl.disable();
        } else {
          const etapaPreSelecionada = this.etapaIdParaCarregar != null
            ? this.etapasOpcoes.find(e => e.id === this.etapaIdParaCarregar)
            : null;
          etapaControl.setValue(etapaPreSelecionada ?? null);
          etapaControl.enable();
        }
        this.etapaIdParaCarregar = undefined;
        if (cargaInicial) this.alteracaoService.camposNaoAlterados();
      },
      error: () => {
        this.resolvendoRedirecionamento = false;
        this.bloqueadoSemEtapa = true;
        this.mensagemBloqueio = 'Erro ao resolver canal de envio para este template.';
        if (cargaInicial) this.alteracaoService.camposNaoAlterados();
      }
    });
  }

  private etapaIdParaCarregar?: number;

  carregarLembrete(id: number) {
    this.carregando = true;
    this.lembreteService.buscarPorId(id).subscribe({
      next: lembrete => {
        const contato = new Contato();
        contato.id = lembrete.contato.id!;
        contato.nome = lembrete.contato.nome;
        contato.numero = lembrete.contatoNumero ?? '';
        this.contatoSelecionado = contato;
        this.lembretePodeProcessar = lembrete.podeProcessar;
        this.lembretePodeExcluir = lembrete.podeExcluir;

        const dataHora = moment(lembrete.dataHora);
        this.etapaIdParaCarregar = lembrete.etapa?.id;
        this.form.patchValue({
          tipo: lembrete.tipo,
          titulo: lembrete.titulo,
          data: dataHora.clone(),
          hora: dataHora.format('HH:mm'),
          anotacao: lembrete.anotacao ?? '',
        });
        if (lembrete.template?.id) {
          this.form.get('templateId')!.setValue(lembrete.template);
          this.onTemplateSelecionado(lembrete.template, true);
        }
        this.carregando = false;
        this.alteracaoService.camposNaoAlterados();
        if (this.isVisualizacao) {
          this.form.disable({ emitEvent: false });
          this.alteracaoService.camposNaoAlterados();
        }
      },
      error: () => this.carregando = false
    });
  }

  aoAlterarContato(contato: Contato | null) {
    this.contatoSelecionado = contato;
  }

  private buscarTemplatesSeNecessario() {
    if (this.isVisualizacao || !this.exibirTemplate || this.templatesCarregados || this.carregandoTemplates) {
      return;
    }
    this.carregandoTemplates = true;
    this.templateMensagemService.buscarSelectTemplatesLembrete().subscribe({
      next: res => {
        this.templates = res ?? [];
        this.templatesCarregados = true;
        this.carregandoTemplates = false;
      },
      error: () => this.carregandoTemplates = false
    });
  }

  private buscarDetalheTemplate(templateId?: number) {
    const versao = ++this.versaoTemplateDetalhe;
    this.templateDetalhe = null;
    this.carregandoTemplateDetalhe = !!templateId;
    if (!templateId) {
      return;
    }
    this.templateMensagemService.buscarTemplateLembrete(templateId).subscribe({
      next: detalhe => {
        if (versao !== this.versaoTemplateDetalhe) return;
        this.templateDetalhe = detalhe ?? null;
        this.carregandoTemplateDetalhe = false;
      },
      error: () => {
        if (versao !== this.versaoTemplateDetalhe) return;
        this.carregandoTemplateDetalhe = false;
      }
    });
  }

  get templateSelecionado(): TemplateSalvamentoModel | undefined {
    return this.templateDetalhe ?? undefined;
  }

  get nomeTemplateEscolhido(): string {
    const template = this.form?.get('templateId')?.value as IdNome | null;
    return template?.id ? `${template.id} - ${template.nome ?? ''}` : '';
  }

  get previaMensagem(): string {
    return this.templateSelecionado?.components?.find(c => c.type === 'BODY')?.text ?? '';
  }

  get headerComponent() {
    return this.templateSelecionado?.components?.find(c => c.type === 'HEADER');
  }

  get horaFormatadaPreview(): string {
    return (this.form?.get('hora')?.value as string) || moment().format('HH:mm');
  }

  get urlImagemHeader(): string {
    if (this.headerComponent?.format !== 'IMAGE') {
      return '';
    }
    return this.montarUrlImagemHeaderTemplate(this.headerComponent?.example?.[0]);
  }

  private montarUrlImagemHeaderTemplate(exemplo?: TemplateComponenteExemploModel): string {
    if (exemplo?.imageUuid && exemplo?.imageExtensao) {
      const extensao = exemplo.imageExtensao.replace(/^\.+/, '');
      return `${environment.urlRepositorioClaribot}/${this.usuario?.grupo}/templates-anexo/${exemplo.imageUuid}.${extensao}`;
    }
    return exemplo?.urlImagem ?? '';
  }

  abrirImagemPrevia() {
    if (!this.urlImagemHeader) {
      return;
    }
    this.dialog.open(ModalVizualizadorComponent, {
      width: 'auto',
      height: 'auto',
      disableClose: false,
      data: { url: this.urlImagemHeader, maxWidth: '60vw', maxHeight: '90vh', isDelete: false },
    });
  }

  get exibirTemplate(): boolean {
    return this.form?.get('tipo')?.value === TipoLembreteEnum.PARA_CLIENTE;
  }

  get tituloModal(): string {
    if (this.isVisualizacao) {
      return 'Visualizar Lembrete';
    }
    return this.isEdicao ? 'Editar Lembrete' : 'Novo Lembrete';
  }

  get dataFormatadaTexto(): string {
    const valor = this.form?.get('data')?.value;
    return valor ? moment(valor).format('DD/MM/YYYY') : '';
  }

  get etapaSelecionadaTexto(): string {
    return this.form?.get('etapaId')?.value?.descricaoEtapaCanal ?? '';
  }

  get carregandoDetalhesTemplate(): boolean {
    return (this.isEdicao || this.isVisualizacao) && this.exibirTemplate
      && (this.carregandoTemplateDetalhe || this.resolvendoRedirecionamento);
  }

  get erroTitulo(): string {
    const c = this.form.get('titulo');
    return c?.invalid && c?.touched ? 'Informe um título com pelo menos 3 caracteres.' : '';
  }

  get erroData(): string {
    const c = this.form.get('data');
    return c?.invalid && c?.touched ? 'Informe a data do lembrete.' : '';
  }

  get erroHora(): string {
    const c = this.form.get('hora');
    return c?.invalid && c?.touched ? 'Informe o horário do lembrete.' : '';
  }

  get erroTemplate(): string {
    const c = this.form.get('templateId');
    return c?.invalid && c?.touched ? 'Selecione um template.' : '';
  }

  salvar() {
    this.form.markAllAsTouched();
    if (!this.contatoSelecionado) {
      this.msgService.msgAdvertencia('Selecione um contato.');
      return;
    }
    if (this.form.invalid) {
      return;
    }

    if (this.exibirTemplate) {
      if (this.bloqueadoSemEtapa) {
        this.msgService.msgAdvertencia(this.mensagemBloqueio || 'Não é possível enviar este template.');
        return;
      }
      if (this.etapasOpcoes.length > 0 && !this.form.get('etapaId')?.value) {
        this.msgService.msgAdvertencia('Selecione a etapa/canal de envio.');
        return;
      }
    }

    const valores = this.form.getRawValue();
    const dataHora = moment(valores.data);
    const partesHora = (valores.hora as string).split(':').map((v: string) => parseInt(v.trim(), 10));
    dataHora.set({ hour: partesHora[0] || 0, minute: partesHora[1] || 0, second: 0, millisecond: 0 });

    if (dataHora.isBefore(moment())) {
      this.msgService.msgAdvertencia('A data e hora do lembrete não podem estar no passado.');
      return;
    }

    const templateSelecionado = valores.templateId as IdNome | null;
    const etapaSelecionada = valores.etapaId as any;
    const payload: LembreteCriacao = {
      contatoId: this.contatoSelecionado.id,
      tipo: valores.tipo,
      titulo: valores.titulo,
      dataHora: dataHora.format('YYYY-MM-DDTHH:mm:ss'),
      templateId: valores.tipo === TipoLembreteEnum.PARA_CLIENTE ? (templateSelecionado?.id ?? undefined) : undefined,
      etapaId: valores.tipo === TipoLembreteEnum.PARA_CLIENTE ? (etapaSelecionada?.id ?? undefined) : undefined,
      anotacao: valores.anotacao || undefined,
    };

    const precisaConfirmar = !this.isEdicao || this.alteracaoService.isCamposAlteracao();
    if (!precisaConfirmar) {
      this.executarSalvar(payload);
      return;
    }

    const texto = this.isEdicao ? 'Deseja realmente editar este lembrete?' : 'Deseja realmente salvar este lembrete?';
    this.dialog.open(ModalConfirmacaoComponent, { data: { texto } }).afterClosed().subscribe(confirmou => {
      if (confirmou) {
        this.executarSalvar(payload);
      }
    });
  }

  private executarSalvar(payload: LembreteCriacao) {
    this.salvando = true;
    const lembreteId = this.data?.['lembreteId'];
    if (lembreteId) {
      this.lembreteService.atualizar(lembreteId, payload).subscribe({
        next: () => this.finalizarSalvamento('Lembrete atualizado com sucesso.'),
        error: err => this.tratarErroSalvar(err)
      });
    } else {
      this.lembreteService.criar(payload).subscribe({
        next: () => this.finalizarSalvamento('Lembrete criado com sucesso.'),
        error: err => this.tratarErroSalvar(err)
      });
    }
  }

  private tratarErroSalvar(err: any) {
    this.salvando = false;
    this.msgService.msgErro(extrairMensagemErro(err, 'Não foi possível salvar o lembrete.'));
  }

  private finalizarSalvamento(mensagem: string) {
    this.msgService.msgSucesso(mensagem);
    this.salvando = false;
    this.alteracaoService.camposNaoAlterados();
    this.dialogRef.close(true);
  }

  confirmarProcessar() {
    this.dialog.open(ModalConfirmacaoComponent, {
      data: { texto: 'Deseja realmente processar este lembrete agora?' }
    }).afterClosed().subscribe(confirmou => {
      if (confirmou) {
        this.processarAgora();
      }
    });
  }

  private processarAgora() {
    const lembreteId = this.data?.['lembreteId'];
    if (!lembreteId) {
      return;
    }
    this.processando = true;
    this.lembreteService.processar(lembreteId).subscribe({
      next: () => {
        this.msgService.msgSucesso('Lembrete processado com sucesso.');
        this.processando = false;
        this.alteracaoService.camposNaoAlterados();
        this.dialogRef.close(true);
      },
      error: err => {
        this.processando = false;
        this.msgService.msgErro(extrairMensagemErro(err, 'Não foi possível processar o lembrete.'));
      }
    });
  }

  confirmarExcluir() {
    this.dialog.open(ModalConfirmacaoComponent, {
      data: { texto: 'Deseja realmente excluir este lembrete?' }
    }).afterClosed().subscribe(confirmou => {
      if (confirmou) {
        this.excluirLembrete();
      }
    });
  }

  private excluirLembrete() {
    const lembreteId = this.data?.['lembreteId'];
    if (!lembreteId) {
      return;
    }
    this.excluindo = true;
    this.lembreteService.excluir(lembreteId).subscribe({
      next: () => {
        this.msgService.msgSucesso('Lembrete excluído com sucesso.');
        this.excluindo = false;
        this.alteracaoService.camposNaoAlterados();
        this.dialogRef.close(true);
      },
      error: err => {
        this.excluindo = false;
        this.msgService.msgErro(extrairMensagemErro(err, 'Não foi possível excluir o lembrete.'));
      }
    });
  }

  fechar() {
    this.alteracaoService.confirmarFechamento(this.dialogRef, 'Deseja realmente cancelar?');
  }
}
